import React, { useState, useEffect } from 'react';
import { TextField, Popover, Box, Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br';
import { StyledContainer } from './styles';

dayjs.locale('pt-br');

type DateRange<T> = [T | null, T | null];

export interface DateRangePickerProps {
  /** Valor inicial do range de datas */
  value?: DateRange<Dayjs>;
  /** Função chamada quando o valor muda */
  onChange: (value: DateRange<Dayjs>) => void;
  /** Label do campo */
  label?: string;
  /** Se o componente está desabilitado */
  disabled?: boolean;
  /** Placeholder quando nenhuma data está selecionada */
  placeholder?: string;
  /** Limite máximo de dias no range */
  maxDays?: number;
}

export const DateRangePicker = ({
  value,
  onChange,
  label = "Período",
  disabled = false,
  placeholder = "Selecione um período",
  maxDays = 30
}: DateRangePickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [internalValue, setInternalValue] = useState<DateRange<Dayjs>>(value || [null, null]);
  const [error, setError] = useState<string>("");

  // Sincroniza internalValue quando value muda externamente
  useEffect(() => {
    if (value) {
      setInternalValue(value);
      setError("");
    }
  }, [value]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setError("");
    if (value) {
      setInternalValue(value);
    }
  };

  const handleChange = (newValue: DateRange<Dayjs>) => {
    setInternalValue(newValue);

    // Valida se o range não excede o limite máximo
    if (newValue[0] && newValue[1]) {
      const diffDays = newValue[1].diff(newValue[0], 'day');
      if (diffDays > maxDays + 1) {
        setError(`O período não pode exceder ${maxDays} dias`);
        return;
      }
    }

    // Limpa o erro se o range é válido
    setError("");
    onChange(newValue);
  };

  const formatDateRange = (range: DateRange<Dayjs>): string => {
    if (!range[0] && !range[1]) {
      return placeholder;
    }

    if (range[0] && !range[1]) {
      return range[0].format('DD/MM/YYYY') + ' - ...';
    }

    if (range[0] && range[1]) {
      return `${range[0].format('DD/MM/YYYY')} - ${range[1].format('DD/MM/YYYY')}`;
    }

    return placeholder;
  };

  const open = Boolean(anchorEl);

  return (
    <StyledContainer>
      <TextField
        fullWidth
        label={label}
        value={formatDateRange(internalValue)}
        onClick={handleClick}
        disabled={disabled}
        error={!!error}
        helperText={error}
        InputProps={{
          readOnly: true,
        }}
        sx={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          '& .MuiInputBase-input': {
            cursor: disabled ? 'not-allowed' : 'pointer',
          }
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  label="Data início"
                  value={internalValue[0]}
                  onChange={(newValue) => handleChange([newValue, internalValue[1]])}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="Data fim"
                  value={internalValue[1]}
                  onChange={(newValue) => handleChange([internalValue[0], newValue])}
                  maxDate={dayjs()}
                  minDate={internalValue[0] || undefined}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Box>
      </Popover>
    </StyledContainer>
  );
};
